import { useEffect, useRef, useState } from 'react';
import { api, ApiCallError } from '../api/client';
import { formatMoney } from '@xiaopeiban/shared';
import type { ChildSummary } from '@xiaopeiban/shared';

interface CloudBottle {
  id: number;
  transcript: string;
  duration_ms: number;
  mime: string;
  audio_b64: string;
  created_at: number;
}

interface BottleState {
  loading: boolean;
  shareEnabled: boolean;
  bottles: CloudBottle[];
}

export default function ChildrenPage() {
  const [children, setChildren] = useState<ChildSummary[]>([]);
  const [quota, setQuota] = useState<{ used: number; max: number }>({ used: 0, max: 2 });
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [birthDate, setBirthDate] = useState('');
  const [avatar, setAvatar] = useState('');
  const [grantOpen, setGrantOpen] = useState<number | null>(null);
  const [grantAmount, setGrantAmount] = useState('');
  const [grantReason, setGrantReason] = useState('');
  const [success, setSuccess] = useState<string | null>(null);

  // 新增孩子
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newBirthDate, setNewBirthDate] = useState('');
  const [newAvatar, setNewAvatar] = useState('');
  const [creating, setCreating] = useState(false);

  // 声音留言瓶
  const [bottleStates, setBottleStates] = useState<Record<number, BottleState>>({});
  const [bottleOpen, setBottleOpen] = useState<number | null>(null);
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);

  async function load() {
    try {
      const r = await api.listChildren();
      setChildren(r.children);
      if (r.quota) setQuota(r.quota);
    } catch (e) { setErr(niceError(e)); }
  }

  useEffect(() => { load(); }, []);

  function startEdit(c: ChildSummary) {
    setEditing(c.id);
    setBirthDate(c.birth_date || '');
    setAvatar(c.avatar || '');
  }

  async function saveEdit() {
    if (editing == null) return;
    try {
      await api.updateChildProfile(editing, {
        birth_date: birthDate || null,
        avatar: avatar || null,
      });
      setEditing(null);
      flash('保存成功');
      load();
    } catch (e) { setErr(niceError(e)); }
  }

  async function grant() {
    if (grantOpen == null) return;
    const rm = parseFloat(grantAmount);
    if (!Number.isFinite(rm) || rm === 0) { setErr('金额必须是非零数字，例如 5 或 -2.50'); return; }
    const sen = Math.round(rm * 100);
    try {
      await api.grantPoints(grantOpen, sen, grantReason.trim() || undefined);
      setGrantOpen(null);
      setGrantAmount('');
      setGrantReason('');
      flash(`已${sen > 0 ? '加' : '扣'} RM ${Math.abs(rm).toFixed(2)}`);
      load();
    } catch (e) { setErr(niceError(e)); }
  }

  async function createChild() {
    setErr(null);
    if (!newName.trim()) { setErr('请填名字'); return; }
    if (newPassword.length < 4) { setErr('密码至少 4 位'); return; }
    setCreating(true);
    try {
      await api.createChild({
        name: newName.trim(),
        password: newPassword,
        birth_date: newBirthDate || null,
        avatar: newAvatar.trim() || null,
      });
      setCreateOpen(false);
      setNewName(''); setNewPassword(''); setNewBirthDate(''); setNewAvatar('');
      flash('孩子账号已创建 ✅');
      load();
    } catch (e) {
      setErr(niceError(e));
    } finally {
      setCreating(false);
    }
  }

  async function toggleShare(c: ChildSummary, enable: boolean) {
    try {
      await api.setVoiceBottleShare(c.id, enable);
      flash(enable ? '已打开收听权限' : '已关闭收听权限');
      // optimistic update
      setChildren(prev => prev.map(x => x.id === c.id ? { ...x, voice_bottle_share: enable ? 1 : 0 } : x));
      // if just enabled and panel is open, refresh bottles
      if (enable && bottleOpen === c.id) await loadBottles(c.id);
      else if (!enable) {
        // collapse stale data
        setBottleStates(prev => ({ ...prev, [c.id]: { loading: false, shareEnabled: false, bottles: [] } }));
      }
    } catch (e) { setErr(niceError(e)); }
  }

  async function loadBottles(childId: number) {
    setBottleStates(prev => ({
      ...prev,
      [childId]: { loading: true, shareEnabled: prev[childId]?.shareEnabled ?? false, bottles: [] },
    }));
    try {
      const r = await api.listChildVoiceBottles(childId);
      setBottleStates(prev => ({
        ...prev,
        [childId]: {
          loading: false,
          shareEnabled: r.share_enabled,
          bottles: r.bottles,
        },
      }));
    } catch (e) {
      setErr(niceError(e));
      setBottleStates(prev => ({
        ...prev,
        [childId]: { loading: false, shareEnabled: false, bottles: [] },
      }));
    }
  }

  function openBottlePanel(c: ChildSummary) {
    if (bottleOpen === c.id) {
      stopPlaying();
      setBottleOpen(null);
      return;
    }
    setBottleOpen(c.id);
    if (c.voice_bottle_share) void loadBottles(c.id);
    else setBottleStates(prev => ({ ...prev, [c.id]: { loading: false, shareEnabled: false, bottles: [] } }));
  }

  function playBottle(b: CloudBottle) {
    stopPlaying();
    try {
      const binary = atob(b.audio_b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: b.mime || 'audio/webm' }));
      const el = new Audio(url);
      audioElRef.current = el;
      const key = `${b.id}`;
      setPlayingKey(key);
      el.onended = () => { URL.revokeObjectURL(url); setPlayingKey(null); };
      el.onerror = () => { URL.revokeObjectURL(url); setPlayingKey(null); };
      void el.play();
    } catch {
      setErr('播放失败');
    }
  }

  function stopPlaying() {
    if (audioElRef.current) {
      try { audioElRef.current.pause(); } catch { /* noop */ }
      audioElRef.current = null;
    }
    setPlayingKey(null);
  }

  function flash(msg: string) {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 2000);
  }

  const atQuota = quota.used >= quota.max;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">👶 孩子管理</div>
        <div className="page-subtitle">设置资料、手动调整马币、收听声音留言瓶</div>
      </div>

      {err && <div className="error-box">{err}</div>}
      {success && <div className="success-box">{success}</div>}

      <div className="card" style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 8,
      }}>
        <div>
          <div style={{ fontWeight: 700 }}>已创建 {quota.used} / {quota.max} 个孩子</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            {atQuota
              ? '已经达到上限啦～如需更多请联系开发者'
              : `还可以再创建 ${quota.max - quota.used} 个`}
          </div>
        </div>
        <button
          className="btn btn-primary"
          disabled={atQuota}
          onClick={() => { setErr(null); setCreateOpen(true); }}
        >
          + 新增孩子
        </button>
      </div>

      {createOpen && (
        <div className="card" style={{ background: 'var(--bg-soft, #fffbe6)' }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>新增孩子账号</div>
          <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>名字 *</label>
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            maxLength={20}
            placeholder="例如：小米"
          />
          <label style={{ fontWeight: 700, display: 'block', marginTop: 12, marginBottom: 4 }}>登录密码 *（至少 4 位）</label>
          <input
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            autoComplete="new-password"
          />
          <label style={{ fontWeight: 700, display: 'block', marginTop: 12, marginBottom: 4 }}>生日（可选）</label>
          <input
            type="date"
            value={newBirthDate}
            onChange={e => setNewBirthDate(e.target.value)}
          />
          <label style={{ fontWeight: 700, display: 'block', marginTop: 12, marginBottom: 4 }}>头像（emoji 可选）</label>
          <input
            placeholder="例如 🐹"
            value={newAvatar}
            onChange={e => setNewAvatar(e.target.value)}
            maxLength={200}
          />
          <div className="flex" style={{ marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setCreateOpen(false)} disabled={creating}>取消</button>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={createChild} disabled={creating}>
              {creating ? <span className="spinner" /> : '创建账号'}
            </button>
          </div>
        </div>
      )}

      {children.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <div className="muted">还没有孩子账号，点上面的「+ 新增孩子」开始吧</div>
        </div>
      )}

      {children.map(c => {
        const shareOn = !!c.voice_bottle_share;
        const bs = bottleStates[c.id];
        const isOpen = bottleOpen === c.id;
        return (
          <div key={c.id} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>
                  {c.avatar || '👶'} {c.name}
                </div>
                <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                  余额 {formatMoney(c.balance)}
                  {c.birth_date && ` · 生日 ${c.birth_date}`}
                </div>
              </div>
              <div className="flex">
                <button className="btn btn-secondary" onClick={() => startEdit(c)}>编辑</button>
                <button className="btn btn-primary" onClick={() => setGrantOpen(c.id)}>调整马币</button>
              </div>
            </div>

            {editing === c.id && (
              <div style={{ marginTop: 16, padding: 16, background: 'var(--bg)', borderRadius: 12 }}>
                <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>生日 (YYYY-MM-DD)</label>
                <input
                  type="date"
                  value={birthDate}
                  onChange={e => setBirthDate(e.target.value)}
                />
                <label style={{ fontWeight: 700, display: 'block', marginTop: 12, marginBottom: 4 }}>
                  头像（emoji 或 URL）
                </label>
                <input
                  placeholder="例如 🐹"
                  value={avatar}
                  onChange={e => setAvatar(e.target.value)}
                  maxLength={200}
                />
                <div className="flex" style={{ marginTop: 12 }}>
                  <button className="btn btn-secondary" onClick={() => setEditing(null)}>取消</button>
                  <button className="btn btn-primary" style={{ flex: 1 }} onClick={saveEdit}>保存</button>
                </div>
              </div>
            )}

            {grantOpen === c.id && (
              <div style={{ marginTop: 16, padding: 16, background: 'var(--bg)', borderRadius: 12 }}>
                <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>
                  金额（RM，正数加分，负数扣分）
                </label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="例如 5 或 -2.50"
                  value={grantAmount}
                  onChange={e => setGrantAmount(e.target.value)}
                />
                <label style={{ fontWeight: 700, display: 'block', marginTop: 12, marginBottom: 4 }}>
                  原因（可选）
                </label>
                <input
                  placeholder="例如：本周表现好"
                  value={grantReason}
                  onChange={e => setGrantReason(e.target.value)}
                  maxLength={200}
                />
                <div className="flex" style={{ marginTop: 12 }}>
                  <button className="btn btn-secondary" onClick={() => setGrantOpen(null)}>取消</button>
                  <button className="btn btn-primary" style={{ flex: 1 }} onClick={grant}>确认</button>
                </div>
              </div>
            )}

            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--border, #eee)' }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexWrap: 'wrap', gap: 8,
              }}>
                <div>
                  <div style={{ fontWeight: 700 }}>🍶 声音留言瓶</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    {shareOn
                      ? '收听权限已打开 — 你可以听 TA 留给小伙伴的悄悄话'
                      : '默认关闭 — 这是 TA 和小伙伴之间的悄悄话'}
                  </div>
                </div>
                <div className="flex">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700 }}>
                    <input
                      type="checkbox"
                      checked={shareOn}
                      onChange={e => toggleShare(c, e.target.checked)}
                    />
                    允许我收听
                  </label>
                  <button className="btn btn-secondary btn-sm" onClick={() => openBottlePanel(c)}>
                    {isOpen ? '收起' : '展开'}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div style={{ marginTop: 12 }}>
                  {!shareOn && (
                    <div className="muted" style={{ fontSize: 13 }}>
                      打开「允许我收听」后才能听这些留言。
                    </div>
                  )}
                  {shareOn && bs?.loading && (
                    <div className="muted"><span className="spinner spinner-sm" /> 加载中…</div>
                  )}
                  {shareOn && bs && !bs.loading && bs.bottles.length === 0 && (
                    <div className="muted" style={{ fontSize: 13 }}>还没有云端留言（最多保留最近 5 条）</div>
                  )}
                  {shareOn && bs && !bs.loading && bs.bottles.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {bs.bottles.map(b => {
                        const isPlaying = playingKey === `${b.id}`;
                        return (
                          <div key={b.id} style={{
                            padding: 10, background: 'var(--bg, #fafafa)',
                            borderRadius: 8, fontSize: 13,
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                              <div className="muted" style={{ fontSize: 12 }}>
                                {formatTimestamp(b.created_at)} · {Math.round(b.duration_ms / 1000)}s
                              </div>
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => isPlaying ? stopPlaying() : playBottle(b)}
                              >
                                {isPlaying ? '⏸ 暂停' : '▶ 播放'}
                              </button>
                            </div>
                            {b.transcript ? (
                              <div style={{ marginTop: 6, lineHeight: 1.5 }}>"{b.transcript}"</div>
                            ) : (
                              <div className="muted" style={{ marginTop: 6, fontStyle: 'italic' }}>（无文字识别）</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatTimestamp(ts: number): string {
  try {
    const d = new Date(ts);
    return d.toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function niceError(e: unknown): string {
  if (e instanceof ApiCallError) return e.message;
  return (e as Error)?.message || '出错了';
}
